/**
 * UNIFIED COORDINATE SYSTEM FOR CATALOG EDITOR AND VIEWER
 * 
 * This module provides the SINGLE SOURCE OF TRUTH for:
 * - Base canvas dimensions
 * - Coordinate transformations
 * - Hotspot positioning calculations
 * 
 * CRITICAL: All components (editor, preview, viewer, embedded) MUST use these utilities
 * to ensure pixel-perfect alignment across all viewing modes.
 */

// BASE CANVAS DIMENSIONS (SINGLE SOURCE OF TRUTH)
// All hotspot coordinates are stored relative to these dimensions
export const BASE_CANVAS = {
  WIDTH: 1200,
  HEIGHT: 1800,
  ASPECT_RATIO: 1200 / 1800 // 2:3 ratio
};

/**
 * Calculate the actual rendered dimensions of a page container
 * while maintaining the base aspect ratio
 * 
 * @param {HTMLElement} container - The container element
 * @returns {{width: number, height: number, scale: number}}
 */
export const getRenderedDimensions = (container) => {
  if (!container) {
    return { width: BASE_CANVAS?.WIDTH, height: BASE_CANVAS?.HEIGHT, scale: 1 };
  }

  // Use layout dimensions (offsetWidth/offsetHeight) instead of getBoundingClientRect()
  // because getBoundingClientRect() returns visual (post-transform) dimensions that include
  // any CSS transform: scale() applied to ancestor elements. This causes hotspot positions
  // to drift during zoom, since hotspot children use `position: absolute` which is relative
  // to the layout (pre-transform) coordinate space.
  let containerWidth = container.offsetWidth;
  let containerHeight = container.offsetHeight;

  // Fallback for elements not yet visible in the DOM (e.g., hidden or not yet mounted)
  if (!containerWidth || !containerHeight) {
    console.warn('[COORD_SYS] offsetWidth/offsetHeight is 0, falling back to getBoundingClientRect');
    const rect = container.getBoundingClientRect();
    containerWidth = rect?.width;
    containerHeight = rect?.height;
  }

  const containerAspectRatio = containerWidth / containerHeight;

  let renderedWidth, renderedHeight;

  if (containerAspectRatio > BASE_CANVAS?.ASPECT_RATIO) {
    // Container is wider - fit to height
    renderedHeight = containerHeight;
    renderedWidth = renderedHeight * BASE_CANVAS?.ASPECT_RATIO;
  } else {
    // Container is taller - fit to width
    renderedWidth = containerWidth;
    renderedHeight = renderedWidth / BASE_CANVAS?.ASPECT_RATIO;
  }

  const scale = renderedWidth / BASE_CANVAS?.WIDTH;

  return {
    width: renderedWidth,
    height: renderedHeight,
    scale
  };
};

/**
 * Convert mouse event coordinates to base canvas coordinates
 * Used in EDITOR when placing/dragging hotspots
 * 
 * @param {MouseEvent} event - Mouse event
 * @param {HTMLElement} container - Page container element
 * @returns {{x: number, y: number}} - Coordinates in base canvas space
 */
export const mouseToBaseCoordinates = (event, container) => {
  if (!container || !event) {
    return { x: 0, y: 0 };
  }

  const rect = container?.getBoundingClientRect();
  const { scale } = getRenderedDimensions(container);

  // Get mouse position relative to container
  const mouseX = event?.clientX - rect?.left;
  const mouseY = event?.clientY - rect?.top;

  // Convert to base canvas coordinates
  const baseX = Math.round(mouseX / scale);
  const baseY = Math.round(mouseY / scale);

  return { x: baseX, y: baseY };
};

/**
 * Convert base canvas coordinates to rendered pixel coordinates
 * Used in VIEWER to position hotspots correctly
 * 
 * @param {{x: number, y: number}} baseCoords - Coordinates in base canvas space
 * @param {HTMLElement} container - Page container element
 * @returns {{x: number, y: number}} - Coordinates in rendered pixel space
 */
export const baseToRenderedCoordinates = (baseCoords, container) => {
  if (!baseCoords || !container) {
    return { x: 0, y: 0 };
  }

  const { scale } = getRenderedDimensions(container);

  // Convert from base canvas to rendered coordinates
  const renderedX = Math.round(baseCoords?.x * scale);
  const renderedY = Math.round(baseCoords?.y * scale);

  return { x: renderedX, y: renderedY };
};

/**
 * Scale hotspot dimensions from base canvas to rendered size
 * 
 * @param {{width: number, height: number}} baseDimensions - Dimensions in base canvas space
 * @param {HTMLElement} container - Page container element
 * @returns {{width: number, height: number}} - Dimensions in rendered pixel space
 */
export const scaleHotspotDimensions = (baseDimensions, container) => {
  if (!baseDimensions || !container) {
    return { width: 200, height: 200 };
  }

  const { scale } = getRenderedDimensions(container);

  return {
    width: Math.round(baseDimensions?.width * scale),
    height: Math.round(baseDimensions?.height * scale)
  };
};

/**
 * Get the style object for positioning a hotspot in the viewer
 * This is the FINAL AUTHORITY for hotspot rendering
 * 
 * @param {Object} hotspot - Hotspot object with x, y, width, height in base coordinates
 * @param {HTMLElement} container - Page container element
 * @returns {Object} - Style object with left, top, width, height in pixels
 */
export const getHotspotStyle = (hotspot, container) => {
  if (!hotspot || !container) {
    return {};
  }

  const position = baseToRenderedCoordinates(
    { x: hotspot?.x, y: hotspot?.y },
    container
  );

  const dimensions = scaleHotspotDimensions(
    { width: hotspot?.width || 200, height: hotspot?.height || 200 },
    container
  );

  return {
    left: `${position?.x}px`,
    top: `${position?.y}px`,
    width: `${dimensions?.width}px`,
    height: `${dimensions?.height}px`
  };
};

/**
 * Validate that coordinates are within base canvas bounds
 * 
 * @param {{x: number, y: number}} coords - Coordinates to validate
 * @param {{width: number, height: number}} hotspotSize - Size of the hotspot
 * @returns {{x: number, y: number}} - Clamped coordinates
 */
export const clampCoordinates = (coords, hotspotSize = { width: 200, height: 200 }) => {
  const maxX = BASE_CANVAS?.WIDTH - hotspotSize?.width;
  const maxY = BASE_CANVAS?.HEIGHT - hotspotSize?.height;

  return {
    x: Math.max(0, Math.min(maxX, Math.round(coords?.x))),
    y: Math.max(0, Math.min(maxY, Math.round(coords?.y)))
  };
};

/**
 * Get default hotspot dimensions in base canvas coordinates
 * 
 * @param {string} type - Hotspot type (link, buy, info, image, video)
 * @returns {{width: number, height: number}}
 */
export const getDefaultHotspotSize = (type) => {
  switch (type) {
    case 'image': case'video':
      return { width: 300, height: 300 };
    case 'buy': case'link': case'info':
    default:
      return { width: 200, height: 60 };
  }
};

export const getWidthHeightFromAspectRatio = ({ viewerRef, isFrontCover, isBackCover }) => {
  const viewerWidth = viewerRef?.current?.offsetWidth || 1200;
  // Reserve space for navigation bar at the bottom (~70px) and some padding
  const viewerHeight = (viewerRef?.current?.offsetHeight || 900) - 80;

  // Aspect ratio: double spread = 1200w / 900h, single page = 600w / 900h
  const maxWidth = isFrontCover || isBackCover ? 600 : 1200;
  const maxHeight = 900;
  const aspectRatio = maxWidth / maxHeight; // width / height

  // Fit within both viewer width and viewer height
  let width = Math.min(maxWidth, viewerWidth);
  let height = width / aspectRatio;

  if (height > viewerHeight) {
    height = viewerHeight;
    width = height * aspectRatio;
  }

  return { width: Math.round(width), height: Math.round(height) };
};